import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Tag, 
  Palette, 
  Eye,
  EyeOff,
  Save,
  X,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/axios.config';

function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue
  } = useForm();

  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ];

  const iconOptions = [
    'Activity', 'Users', 'Calendar', 'Star', 'Heart',
    'Trophy', 'Book', 'Music', 'Camera', 'Briefcase'
  ];

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('activity-categories/get');
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      toast.error('বিভাগসমূহ আনতে ব্যর্থ');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      if (editingCategory) {
        // Update category
        const response = await api.put(`activity-categories/${editingCategory._id}`, data);
        if (response.data.success) {
          toast.success('বিভাগ সফলভাবে আপডেট হয়েছে');
        }
      } else {
        // Create new category
        const response = await api.post('activity-categories/create', data);
        if (response.data.success) {
          toast.success('নতুন বিভাগ সফলভাবে যুক্ত হয়েছে');
        }
      }
      
      fetchCategories();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'একটি ত্রুটি ঘটেছে');
    }
  };

  // Delete category
  const handleDelete = async (id) => {
    if (!window.confirm('আপনি কি নিশ্চিত যে এই বিভাগটি মুছে ফেলতে চান?')) {
      return;
    }

    try {
      const { data } = await api.delete(`activity-categories/${id}`);
      if (data.success) {
        toast.success('বিভাগ সফলভাবে মুছে ফেলা হয়েছে');
        fetchCategories();
      }
    } catch (error) {
      toast.error('বিভাগ মুছতে ব্যর্থ');
    }
  };

  // Toggle active status
  const handleToggleActive = async (id, isActive) => {
    try {
      const { data } = await api.patch(`activity-categories/${id}`, { isActive: !isActive });
      if (data.success) {
        toast.success('স্ট্যাটাস আপডেট হয়েছে');
        fetchCategories();
      }
    } catch (error) {
      toast.error('স্ট্যাটাস আপডেট করতে ব্যর্থ');
    }
  };

  // Open modal for editing
  const openEditModal = (category) => {
    setEditingCategory(category);
    setValue('name', category.name);
    setValue('description', category.description || '');
    setValue('color', category.color);
    setValue('icon', category.icon);
    setValue('isActive', category.isActive);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    reset();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">কার্যক্রমের বিভাগসমূহ</h1>
          <p className="text-gray-600 mt-1">
            মোট {categories.length} টি বিভাগ রয়েছে
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn btn-primary"
        >
          <Plus size={20} />
          নতুন বিভাগ যুক্ত করুন
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {categories.map((category) => (
          <div key={category._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
            {/* Header with color */}
            <div 
              className="h-16 flex items-center justify-center"
              style={{ backgroundColor: category.color }}
            >
              <Tag size={24} className="text-white" />
            </div>

            {/* Content */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg text-gray-900">{category.name}</h3>
                <div className="flex items-center gap-2">
                  {category.isActive ? (
                    <Eye 
                      size={16} 
                      className="text-green-500 cursor-pointer" 
                      onClick={() => handleToggleActive(category._id, category.isActive)}
                      title="সক্রিয়"
                    />
                  ) : (
                    <EyeOff 
                      size={16} 
                      className="text-gray-400 cursor-pointer" 
                      onClick={() => handleToggleActive(category._id, category.isActive)}
                      title="নিষ্ক্রিয়"
                    />
                  )}
                </div>
              </div>

              {category.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {category.description}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>আইকন: {category.icon}</span>
                <div 
                  className="w-4 h-4 rounded-full border border-gray-300"
                  style={{ backgroundColor: category.color }}
                  title={category.color}
                ></div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <button
                  onClick={() => openEditModal(category)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="সম্পাদনা করুন"
                >
                  <Edit3 size={16} />
                </button>
                
                <button
                  onClick={() => handleDelete(category._id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="মুছে ফেলুন"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">কোন বিভাগ পাওয়া যায়নি</h3>
          <p className="text-gray-600 mb-6">এখনো কোন বিভাগ যুক্ত করা হয়নি।</p>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary"
          >
            <Plus size={20} />
            প্রথম বিভাগ যুক্ত করুন
          </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCategory ? 'বিভাগ সম্পাদনা করুন' : 'নতুন বিভাগ যুক্ত করুন'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  বিভাগের নাম *
                </label>
                <input
                  {...register('name', { 
                    required: 'বিভাগের নাম আবশ্যক',
                    maxLength: { value: 50, message: 'সর্বোচ্চ ৫০ অক্ষর হতে পারে' }
                  })}
                  type="text"
                  placeholder="বিভাগের নাম লিখুন"
                  className="input input-bordered w-full"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={16} className="mr-1" />
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  বিবরণ
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  placeholder="বিভাগের বিবরণ লিখুন (ঐচ্ছিক)"
                  className="textarea textarea-bordered w-full"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  রং নির্বাচন করুন
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {colors.map((color) => (
                    <label key={color} className="cursor-pointer">
                      <input
                        {...register('color', { required: 'রং নির্বাচন আবশ্যক' })}
                        type="radio"
                        value={color}
                        className="sr-only"
                      />
                      <div 
                        className="w-10 h-10 rounded-lg border-2 border-transparent hover:border-gray-300 transition-colors flex items-center justify-center"
                        style={{ backgroundColor: color }}
                      >
                        <Palette size={16} className="text-white opacity-0 peer-checked:opacity-100" />
                      </div>
                    </label>
                  ))}
                </div>
                {errors.color && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={16} className="mr-1" />
                    {errors.color.message}
                  </p>
                )}
              </div>

              {/* Icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  আইকন নির্বাচন করুন
                </label>
                <select
                  {...register('icon', { required: 'আইকন নির্বাচন আবশ্যক' })}
                  className="select select-bordered w-full"
                >
                  <option value="">আইকন নির্বাচন করুন</option>
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
                {errors.icon && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={16} className="mr-1" />
                    {errors.icon.message}
                  </p>
                )}
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center gap-3">
                  <input
                    {...register('isActive')}
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    defaultChecked={true}
                  />
                  <span className="text-sm font-medium text-gray-700">সক্রিয় অবস্থায় রাখুন</span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-outline"
                >
                  বাতিল করুন
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={20} />
                  {editingCategory ? 'আপডেট করুন' : 'যুক্ত করুন'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Categories;